import React, { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AccountingOrgBar from "./AccountingOrgBar";
import { canLoadAccountingData } from "../useAccountingScope";
import { AMOUNT_KEYS, LINE_SIDES } from "../constants";

const emptyLine = () => ({
	account_id: "",
	side: "debit",
	amount_key: "amount",
	line_order: 0,
	memo: "",
});

const PostingTemplatesTab = ({
	requiresOrganizationSelection,
	sessionOrganization,
	organizationId,
	setOrganizationId,
	eventTypes,
	fetchAccounts,
	fetchPostingTemplates,
	savePostingTemplate,
	isSubmitting,
}) => {
	const [templates, setTemplates] = useState([]);
	const [accounts, setAccounts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [dialogOpen, setDialogOpen] = useState(false);
	const [form, setForm] = useState({
		event_type: "",
		name: "",
		description: "",
		is_active: true,
		lines: [emptyLine()],
	});

	const canLoad = canLoadAccountingData(requiresOrganizationSelection, organizationId);

	const load = async () => {
		if (!canLoad) {
			setTemplates([]);
			setAccounts([]);
			return;
		}
		setLoading(true);
		setError("");
		const [templatesResult, accountsResult] = await Promise.all([
			fetchPostingTemplates(organizationId),
			fetchAccounts(organizationId),
		]);
		if (templatesResult.success) setTemplates(templatesResult.templates);
		else setError(templatesResult.message);
		if (accountsResult.success) setAccounts(accountsResult.accounts);
		setLoading(false);
	};

	useEffect(() => {
		load();
	}, [organizationId, requiresOrganizationSelection]);

	const openEdit = (template) => {
		setForm({
			event_type: template.event_type,
			name: template.name || "",
			description: template.description || "",
			is_active: template.is_active !== false,
			lines: (template.lines || []).map((line, idx) => ({
				account_id: line.related_account,
				side: line.side,
				amount_key: line.amount_key,
				line_order: line.line_order ?? idx,
				memo: line.memo || "",
			})),
		});
		setDialogOpen(true);
	};

	const openCreate = () => {
		const defaultEvent = eventTypes[0]?.event_type || "tenant_rent_collected";
		setForm({
			event_type: defaultEvent,
			name: "",
			description: "",
			is_active: true,
			lines: [emptyLine(), { ...emptyLine(), side: "credit", line_order: 1 }],
		});
		setDialogOpen(true);
	};

	const updateLine = (index, patch) => {
		setForm((f) => ({
			...f,
			lines: f.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)),
		}));
	};

	const addLine = () => {
		setForm((f) => ({
			...f,
			lines: [...f.lines, { ...emptyLine(), line_order: f.lines.length }],
		}));
	};

	const removeLine = (index) => {
		setForm((f) => ({
			...f,
			lines: f.lines.filter((_, i) => i !== index),
		}));
	};

	const handleSubmit = async () => {
		if (!form.name.trim() || !form.event_type) {
			setMessage("Name and event type are required.");
			return;
		}
		if (!form.lines.length) {
			setMessage("Add at least one template line.");
			return;
		}
		setMessage("");
		const payload = {
			event_type: form.event_type,
			name: form.name.trim(),
			description: form.description,
			is_active: form.is_active,
			lines: form.lines.map((line, idx) => ({
				account_id: Number(line.account_id),
				side: line.side,
				amount_key: line.amount_key,
				line_order: idx,
				memo: line.memo,
			})),
		};
		if (requiresOrganizationSelection) {
			payload.related_organization = organizationId;
		}
		const result = await savePostingTemplate(payload);
		if (result.success) {
			setDialogOpen(false);
			load();
		} else {
			setMessage(result.message);
		}
	};

	const postableAccounts = accounts.filter((a) => a.is_postable !== false && a.is_active !== false);

	return (
		<Box>
			<Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2}>
				<AccountingOrgBar
					requiresOrganizationSelection={requiresOrganizationSelection}
					organizationId={organizationId}
					onOrganizationChange={setOrganizationId}
					sessionOrganization={sessionOrganization}
				/>
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={openCreate}
					disabled={!canLoad || isSubmitting}
				>
					Add template
				</Button>
			</Box>

			{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
			{message && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMessage("")}>{message}</Alert>}

			{!canLoad ? (
				<Typography color="text.secondary">Select an organisation.</Typography>
			) : loading ? (
				<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
			) : (
				<TableContainer component={Paper}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Event type</TableCell>
								<TableCell>Name</TableCell>
								<TableCell>Lines</TableCell>
								<TableCell>Active</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{templates.length === 0 ? (
								<TableRow>
									<TableCell colSpan={5} align="center">No posting templates yet.</TableCell>
								</TableRow>
							) : (
								templates.map((row) => (
									<TableRow key={row.id} hover>
										<TableCell sx={{ fontFamily: "monospace" }}>{row.event_type}</TableCell>
										<TableCell>{row.name}</TableCell>
										<TableCell>{row.lines?.length ?? 0}</TableCell>
										<TableCell>{row.is_active ? "Yes" : "No"}</TableCell>
										<TableCell align="right">
											<IconButton size="small" onClick={() => openEdit(row)}>
												<EditIcon fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
				<DialogTitle>Posting template</DialogTitle>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} mt={1}>
						<FormControl fullWidth>
							<InputLabel>Event type</InputLabel>
							<Select
								label="Event type"
								value={form.event_type}
								onChange={(e) => setForm((f) => ({ ...f, event_type: e.target.value }))}
							>
								{eventTypes.map((et) => (
									<MenuItem key={et.event_type} value={et.event_type}>
										{et.label || et.event_type}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth />
						<TextField label="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} fullWidth multiline rows={2} />

						<Typography variant="subtitle2">Template lines</Typography>
						{form.lines.map((line, index) => (
							<Box key={index} display="flex" gap={1} flexWrap="wrap" alignItems="center">
								<FormControl size="small" sx={{ minWidth: 180 }}>
									<InputLabel>Account</InputLabel>
									<Select
										label="Account"
										value={line.account_id}
										onChange={(e) => updateLine(index, { account_id: e.target.value })}
									>
										{postableAccounts.map((a) => (
											<MenuItem key={a.id} value={a.id}>
												{a.code} — {a.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<FormControl size="small" sx={{ minWidth: 100 }}>
									<InputLabel>Side</InputLabel>
									<Select label="Side" value={line.side} onChange={(e) => updateLine(index, { side: e.target.value })}>
										{LINE_SIDES.map((s) => (
											<MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
										))}
									</Select>
								</FormControl>
								<FormControl size="small" sx={{ minWidth: 120 }}>
									<InputLabel>Amount key</InputLabel>
									<Select label="Amount key" value={line.amount_key} onChange={(e) => updateLine(index, { amount_key: e.target.value })}>
										{AMOUNT_KEYS.map((k) => (
											<MenuItem key={k.value} value={k.value}>{k.value}</MenuItem>
										))}
									</Select>
								</FormControl>
								<TextField
									size="small"
									label="Memo"
									value={line.memo}
									onChange={(e) => updateLine(index, { memo: e.target.value })}
									sx={{ flex: 1, minWidth: 120 }}
								/>
								<IconButton size="small" onClick={() => removeLine(index)} disabled={form.lines.length <= 1}>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</Box>
						))}
						<Button size="small" onClick={addLine}>Add line</Button>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDialogOpen(false)}>Cancel</Button>
					<Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
						{isSubmitting ? "Saving…" : "Save template"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default PostingTemplatesTab;
